import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Form,
  Row,
  Col,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import BreadCrumbs from "../../../../components/BreadCrumbs";
import ControlledRowTextField from "../../../../components/ControlledRowTextField";
import ControlledSelect from "../../../../components/ControlledSelect";
import ButtonComponent from "../../../../components/ButtonComponent";
import {
  useSaveDimensionValueMutation,
  useGetDimensionsQuery,
  useLazyGetMenuTypesQuery,
  ProductDimension,
  useUploadSetDimensionLogoFileExtntionHttpsMutation,
} from "../../../../redux/services/CustomTypeService";
import IMGUploadComponent from "../../../../components/IMGUploadComponent";
import { uploadImage } from "../../../../components/imageUploadCompont";

interface AddDimensionValueFormData {
  displayName: string;
  description: string;
  displayOrder: { value: number; label: string };
}

const AddDimensionValue: React.FC = () => {
  const { menuType, dimensionId } = useParams<{
    menuType: string;
    dimensionId: string;
  }>();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [menuTypeId, setMenuTypeId] = useState<string | null>(null);
  const [dimension, setDimension] = useState<ProductDimension | null>(null);
  const [uploadedImgDetails, setUploadedImgDetails] = useState({
    imgSrc: "",
    formData: "",
    fileExtention: "",
  });

  // Get restaurant ID from Redux
  const selectedRestaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id
  );

  // Get menu types to find the ID
  const [triggerMenuTypes, { data: menuTypes = [] }] =
    useLazyGetMenuTypesQuery();
  const [uploadLogoFileExtetion] =
    useUploadSetDimensionLogoFileExtntionHttpsMutation();

  // Get dimensions data
  const { data: productDimensions = [] } = useGetDimensionsQuery(
    {
      restaurantId: selectedRestaurantId,
      customMenuItemTypeId: menuTypeId || "",
    },
    {
      skip: !selectedRestaurantId || !menuTypeId,
    }
  );

  // Get the mutation hook
  const [saveDimensionValueMutation] = useSaveDimensionValueMutation();

  // Find the menu type ID based on the name from URL
  useEffect(() => {
    if (selectedRestaurantId && menuType) {
      triggerMenuTypes({ restaurantId: selectedRestaurantId });
    }
  }, [selectedRestaurantId, menuType, triggerMenuTypes]);

  useEffect(() => {
    if (menuTypes && menuTypes.length > 0 && menuType) {
      const foundMenuType = menuTypes.find((mt) => mt.name === menuType);
      if (foundMenuType) {
        setMenuTypeId(foundMenuType.id);
      }
    }
  }, [menuTypes, menuType]);

  // Find the specific dimension
  useEffect(() => {
    if (productDimensions && productDimensions.length > 0 && dimensionId) {
      const foundDimension = productDimensions.find(
        (dim) => dim.id === dimensionId
      );
      setDimension(foundDimension || null);
    }
  }, [productDimensions, dimensionId]);

  const imageUploadCallback = (e: any) => {
    setUploadedImgDetails({
      imgSrc: e.imgSrc,
      formData: e.formData,
      fileExtention: e.fileExtention,
    });
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<AddDimensionValueFormData>({
    defaultValues: {
      displayName: "",
      description: "",
      displayOrder: { value: 1, label: "1" },
    },
  });

  // Options for display order
  const displayOrderOptions = Array.from({ length: 20 }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}`,
  }));

  const onSubmit = async (data: AddDimensionValueFormData) => {
    setIsSubmitting(true);

    try {
      if (!selectedRestaurantId) {
        toast.error("Restaurant not selected. Please refresh and try again.");
        setIsSubmitting(false);
        return;
      }

      if (!dimensionId) {
        toast.error(
          "Dimension ID not found. Please navigate back and try again."
        );
        setIsSubmitting(false);
        return;
      }

      // Validate image is uploaded
      // if (!uploadedImgDetails.formData || !uploadedImgDetails.imgSrc) {
      //   toast.error("Please upload an image for the dimension value");
      //   setIsSubmitting(false);
      //   return;
      // }

      // Upload image and get the reference URL
      const imageReference = await uploadImage(uploadLogoFileExtetion, {
        fileExtention: uploadedImgDetails.fileExtention,
        formData: uploadedImgDetails.formData,
      });

      // Validate image upload was successful
      if (!imageReference) {
        toast.error("Failed to upload image. Please try again.");
        setIsSubmitting(false);
        return;
      }

      const dimensionValueData = {
        restaurantId: selectedRestaurantId,
        dimensionId: dimensionId,
        displayName: data.displayName,
        description: data.description,
        displayOrder: data.displayOrder.value,
        imageReference: imageReference,
        imageUrl: uploadedImgDetails.imgSrc,
      };

      await saveDimensionValueMutation(dimensionValueData).unwrap();

      toast.success("Dimension value created successfully!");
      navigate(`/productCatalogue/product-groups/${menuType}/dimensions`, {
        state: { shouldRefetch: true },
      });
    } catch (error: any) {
      console.error("Error creating dimension value:", error);
      const errorMessage =
        error?.data ||
        error?.message ||
        "Failed to create dimension value. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayNameWatcher = watch("displayName");
  const descriptionWatcher = watch("description");

  if (!dimension) {
    return (
      <>
        <BreadCrumbs 
          hasSubMenus={true}
          customNameMap={{
            [menuTypeId || '']: menuType || 'Menu Type'
          }}
        />
        <Card className="mt-3">
          <CardBody className="text-center py-5">
            <p className="text-danger">Dimension not found</p>
            <Button
              variant="primary"
              onClick={() =>
                navigate(
                  `/productCatalogue/product-groups/${menuType}/dimensions`
                )
              }
            >
              Back to Dimensions
            </Button>
          </CardBody>
        </Card>
      </>
    );
  }

  return (
    <>
      <BreadCrumbs 
        hasSubMenus={true}
        customNameMap={{
          [menuTypeId || '']: menuType || 'Menu Type',
          [dimensionId || '']: dimension?.displayName || 'Dimension'
        }}
      />

      <Card className="mt-3">
        <CardHeader className="header-bg-color d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <div>
              <h5 className="mb-0">Add Value to "{dimension.displayName}"</h5>
              <small className="text-light opacity-75">
                {menuType} → {dimension.displayName}
              </small>
            </div>
          </div>
        </CardHeader>

        <CardBody>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <IMGUploadComponent
              imageUrl={uploadedImgDetails.imgSrc}
              onUpload={imageUploadCallback}
            />
            <hr />
            <Row>
              <Col md={6}>
                <ControlledRowTextField
                  label="Display Name"
                  inputName="displayName"
                  inputType="text"
                  placeholder="e.g., Chicken Thigh, Chicken Breast"
                  control={control}
                  rules={{
                    required: "Display name is required",
                    minLength: {
                      value: 2,
                      message: "Display name must be at least 2 characters",
                    },
                    maxLength: {
                      value: 100,
                      message: "Display name must be less than 100 characters",
                    },
                  }}
                />
                {errors.displayName && (
                  <span className="text-danger small">
                    {errors.displayName.message}
                  </span>
                )}
              </Col>

              <Col md={6}>
                <ControlledSelect
                  label="Display Order"
                  inputName="displayOrder"
                  control={control}
                  optionsList={displayOrderOptions}
                  rules={{ required: "Display order is required" }}
                />
                {errors.displayOrder && (
                  <span className="text-danger small">
                    {errors.displayOrder.message}
                  </span>
                )}
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={12}>
                <ControlledRowTextField
                  label="Description"
                  inputName="description"
                  inputType="textarea"
                  placeholder="Describe this option for customers..."
                  control={control}
                  rules={{
                    required: "Description is required",
                    minLength: {
                      value: 5,
                      message: "Description must be at least 5 characters",
                    },
                    maxLength: {
                      value: 200,
                      message: "Description must be less than 200 characters",
                    },
                  }}
                />
                {errors.description && (
                  <span className="text-danger small">
                    {errors.description.message}
                  </span>
                )}
              </Col>
            </Row>

            <div className="mt-4">
              <div className="alert alert-info">
                <h6 className="mb-2">Value Information:</h6>
                <Row className="small">
                  <Col md={6}>
                    <ul className="mb-0">
                      <li>
                        <strong>Display Name:</strong> Shown to customers
                      </li>
                      <li>
                        <strong>Description:</strong> Helps customers understand
                        the option
                      </li>
                      <li>
                        <strong>Display Order:</strong> Controls sorting order
                      </li>
                    </ul>
                  </Col>
                  <Col md={6}>
                    <ul className="mb-0">
                      <li>
                        <strong>Dimension:</strong>{" "}
                        {dimension?.displayName || "Loading..."}
                      </li>
                      <li>
                        <strong>Menu Type:</strong> {menuType}
                      </li>
                      <li>
                        <strong>Required:</strong>{" "}
                        {dimension?.isRequired ? "Yes" : "No"}
                      </li>
                    </ul>
                  </Col>
                </Row>
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button
                variant="secondary"
                onClick={() =>
                  navigate(
                    `/productCatalogue/product-groups/${menuType}/dimensions`
                  )
                }
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <ButtonComponent
                type="submit"
                isDisabled={
                  isSubmitting || !displayNameWatcher || !descriptionWatcher
                }
                titel={isSubmitting ? "Creating..." : "Create Value"}
                icon={
                  <FontAwesomeIcon
                    icon={isSubmitting ? faSpinner : faSave}
                    spin={isSubmitting}
                  />
                }
              />
            </div>
          </Form>
        </CardBody>
      </Card>
    </>
  );
};

export default AddDimensionValue;
